import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { AssetCategoryView, createCategory } from '../../api/General/assets';
import { useCanManageAssets } from '../../hooks/useCanManageAssets';

interface EquipmentCategoryPickerProps {
  // Every category, at every level. Groups (level 1) are needed for the create path.
  categories: AssetCategoryView[];
  // Categories already added to the list this picker feeds.
  excludeIds: number[];
  onPick: (categoryId: number, name: string) => void;
  // Lets the caller fold a newly created category into its own list.
  onCreated?: (category: AssetCategoryView) => void;
  'data-testid'?: string;
}

/**
 * Picks the equipment a role or a goal needs, and adds a category that does not exist yet without
 * leaving the form.
 *
 * The plain select this replaces had one answer for a category nobody had set up: nothing. The user
 * had to abandon a half-written role, go to asset settings, create the category, and start again.
 * Creating one needs a group (categories are three levels and equipment sits at level 2), so the
 * create row opens a group picker inline rather than guessing a parent.
 */
const EquipmentCategoryPicker: React.FC<EquipmentCategoryPickerProps> = ({
  categories,
  excludeIds,
  onPick,
  onCreated,
  'data-testid': testId = 'equipment-category-picker',
}) => {
  const { t } = useTranslation();
  const canManageAssets = useCanManageAssets();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const [parentId, setParentId] = useState<number | ''>('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const groups = useMemo(() => categories.filter((c) => c.level === 1), [categories]);
  const options = useMemo(
    () => categories.filter((c) => c.level === 2 && !excludeIds.includes(c.id)),
    [categories, excludeIds],
  );

  const trimmed = query.trim();
  const matches = options.filter((c) => c.name.toLowerCase().includes(trimmed.toLowerCase()));
  const exactMatch = categories.some(
    (c) => c.level === 2 && c.name.toLowerCase() === trimmed.toLowerCase(),
  );
  const canCreate = canManageAssets && trimmed.length > 0 && !exactMatch;

  const close = () => {
    setIsOpen(false);
    setPendingName('');
    setError('');
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!wrapperRef.current?.contains(e.relatedTarget as Node)) close();
  };

  const handlePick = (category: AssetCategoryView) => {
    onPick(category.id, category.name);
    setQuery('');
    close();
  };

  const startCreate = () => {
    setPendingName(trimmed);
    setError('');
    // One group means there is nothing to choose; skip the question.
    setParentId(groups.length === 1 ? groups[0].id : '');
  };

  const handleCreate = async () => {
    if (creating || !pendingName || parentId === '') return;
    setCreating(true);
    setError('');
    try {
      const result = await createCategory({ name: pendingName, level: 2, parentId });
      if (result.errors.length > 0 || !result.payload) {
        setError(result.errors[0]?.description || t('equipmentPicker.createFailed'));
        return;
      }
      onCreated?.(result.payload);
      onPick(result.payload.id, result.payload.name);
      setQuery('');
      close();
    } catch {
      setError(t('equipmentPicker.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative" onBlur={handleBlur}>
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setPendingName(''); setError(''); }}
        onFocus={() => setIsOpen(true)}
        placeholder={t('equipmentPicker.placeholder')}
        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        data-testid={testId}
      />

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg max-h-60">
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => handlePick(c)}
              className="w-full px-3 py-2 text-start text-sm text-foreground hover:bg-muted"
              data-testid={`${testId}-option-${c.id}`}
            >
              {c.name}
            </button>
          ))}

          {matches.length === 0 && !canCreate && (
            <p className="px-3 py-2 text-sm text-muted-foreground">{t('equipmentPicker.noMatches')}</p>
          )}

          {canCreate && !pendingName && (
            <button
              type="button"
              onClick={startCreate}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-start text-sm font-medium text-primary hover:bg-muted"
              data-testid={`${testId}-create`}
            >
              <Icon name="Plus" size={14} aria-hidden="true" />
              {t('equipmentPicker.createOption', { name: trimmed })}
            </button>
          )}

          {pendingName && (
            <div className="border-t border-border p-3 space-y-2" data-testid={`${testId}-create-form`}>
              <p className="text-sm font-medium text-foreground">
                {t('equipmentPicker.createOption', { name: pendingName })}
              </p>
              {groups.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('equipmentPicker.noGroups')}</p>
              ) : (
                <>
                  <label className="block text-xs text-muted-foreground" htmlFor={`${testId}-parent`}>
                    {t('equipmentPicker.groupLabel')}
                  </label>
                  <select
                    id={`${testId}-parent`}
                    value={parentId}
                    onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                    data-testid={`${testId}-parent`}
                  >
                    <option value="">{t('equipmentPicker.chooseGroup')}</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={creating || parentId === ''}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
                    data-testid={`${testId}-create-confirm`}
                  >
                    {creating && <Icon name="Loader2" size={14} className="animate-spin" aria-hidden="true" />}
                    {t('equipmentPicker.create')}
                  </button>
                </>
              )}
              {error && <p className="text-xs text-destructive">{error}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EquipmentCategoryPicker;
